import type { MaybeRefOrGetter, WatchSource } from "vue";
import { ref, toValue, watch } from "vue";

// One observer serves every element: a long list of truncatable labels costs one observer, and the
// browser delivers all their size changes in one batch, so the measurements share a single layout.
// An element can have several consumers; it is observed until the last one leaves.
const measurers = new Map<Element, Set<() => void>>();
let sharedObserver: ResizeObserver | undefined;

function measureAll() {
  measurers.forEach((set) => set.forEach((measure) => measure()));
}

function observer() {
  if (!sharedObserver) {
    sharedObserver = new ResizeObserver((entries) => {
      for (const { target } of entries) measurers.get(target)?.forEach((measure) => measure());
    });
    // A web font changes the text width but not the box, so the observer never reports it.
    document.fonts?.addEventListener("loadingdone", measureAll);
  }
  return sharedObserver;
}

function track(target: Element, measure: () => void) {
  const set = measurers.get(target) ?? new Set();
  set.add(measure);
  measurers.set(target, set);
  observer().observe(target);
  return () => {
    set.delete(measure);
    if (set.size > 0) return;
    measurers.delete(target);
    observer().unobserve(target);
  };
}

/**
 * Whether an element's text is cut off by `text-overflow: ellipsis`, kept current as the element is
 * resized. Horizontal, single-line truncation only (it compares widths, so a `-webkit-line-clamp`
 * box is not covered), and the element must be a block or inline-block: an inline element reports
 * zero widths.
 *
 * A resize observer sees the box change, not its text: pass `remeasureOn` (one source or several)
 * for a text that can change while the box stays the same size.
 */
export function useIsTruncated(
  el: MaybeRefOrGetter<HTMLElement | null | undefined>,
  remeasureOn?: WatchSource | WatchSource[],
) {
  const isTruncated = ref(false);
  if (typeof ResizeObserver === "undefined") return isTruncated;

  function measure(target: HTMLElement) {
    const truncated = target.scrollWidth > target.clientWidth;
    if (truncated !== isTruncated.value) isTruncated.value = truncated;
  }

  watch(
    () => toValue(el),
    (target, _previous, onCleanup) => {
      if (!target) {
        isTruncated.value = false;
        return;
      }
      onCleanup(track(target, () => measure(target)));
    },
    { immediate: true, flush: "post" },
  );

  if (remeasureOn) {
    watch(
      remeasureOn,
      () => {
        const target = toValue(el);
        if (target) measure(target);
      },
      { flush: "post" },
    );
  }

  return isTruncated;
}
